import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useOutletContext } from "react-router-dom";
import { useForm } from "react-hook-form";
import { MaterialReactTable } from "material-react-table";
import CountUp from "react-countup";
import { decodeHtml } from "../../utils/decodeHtml";

function SearchProposalList() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const { reset } = useForm();

  // ── Raw data from API ──────────────────────────────────────────────────────
  const [allRoles, setAllRoles] = useState([]);          // full list returned
  const [clients, setClients] = useState([]);
  const [roleManagers, setRoleManagers] = useState([]);
  const [userInfo, setUserInfo] = useState({});

  // ── Derived / display states ───────────────────────────────────────────────
  const [options, setOptions] = useState([]);            // rows shown in table
  const [currentView, setCurrentView] = useState("all");

  // ── Filter states ──────────────────────────────────────────────────────────
  const [filterClient, setFilterClient] = useState("");
  const [filterManager, setFilterManager] = useState("");
  const [filterRoleType, setFilterRoleType] = useState("");

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchOptions = async () => {
    setGlobalLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/roles/searchproposallist`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const lists = response.data.lists || [];
      setAllRoles(lists);
      setOptions(lists);
      setClients(response.data.clients || []);
      setRoleManagers(response.data.role_managers || []);
    } catch (error) {
      console.error("API Error:", error);
      setAllRoles([]);
      setOptions([]);
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  // ── Re-apply filters whenever filter state changes ────────────────────────
  useEffect(() => {
    let filtered = [...allRoles];

    if (filterClient) {
      filtered = filtered.filter((r) =>
        (r.company_info || "").toLowerCase().includes(filterClient.toLowerCase())
      );
    }
    if (filterManager) {
      filtered = filtered.filter(
        (r) => r.role_manager === filterManager
      );
    }
    if (filterRoleType) {
      filtered = filtered.filter((r) => r.type === filterRoleType);
    }

    setOptions(filtered);
  }, [filterClient, filterManager, filterRoleType, allRoles]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const parseCompanyInfo = (companyInfoStr) => {
    if (!companyInfoStr) return { name: "No Client Assigned", logo: "" };
    const [name, logo] = companyInfoStr.split("|||");
    return { name: name || "No Client Assigned", logo: logo || "" };
  };

  const getDaysColor = (days) => {
    if (days === null || days === undefined) return "inherit";
    if (days <= 30) return "#28a745";
    if (days <= 60) return "#ffc107";
    return "#dc3545";
  };

  const clearFilters = () => {
    setFilterClient("");
    setFilterManager("");
    setFilterRoleType("");
  };

  // ── Unique role types derived from data ───────────────────────────────────
  const uniqueRoleTypes = [...new Set(allRoles.map((r) => r.type).filter(Boolean))];

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      accessorKey: "role_id",
      header: "Role ID",
      size: 80,
    },
    {
      accessorKey: "name",
      header: "Role Name",
      size: 220,
      Cell: ({ row }) => (
        <Link
          to={`/workstation/view/${row.original.id}`}
        >
          <b>{decodeHtml(row.original.name)}</b>
        </Link>
      ),
    },
    {
      accessorKey: "company_info",
      header: "Client",
      size: 180,
      Cell: ({ cell }) => {
        const { name, logo } = parseCompanyInfo(cell.getValue());
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            
            <span>{name}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "role_manager",
      header: "Role Manager",
      size: 150,
    },
    {
      accessorKey: "function",
      header: "Function",
      size: 120,
    },
    {
      accessorKey: "industry",
      header: "Industry",
      size: 130,
    },
    // {
    //   accessorKey: "days_since_proposal",
    //   header: "Days Open",
    //   size: 100,
    //   Cell: ({ cell }) => {
    //     const val = cell.getValue();
    //     return (
    //       <span
    //         style={{
    //           color: getDaysColor(val),
    //           fontWeight: "bold",
    //         }}
    //       >
    //         {val !== null && val !== undefined ? `${val}d` : "—"}
    //       </span>
    //     );
    //   },
    // },
    {
      accessorKey: "created_by",
      header: "Created By",
      size: 130,
    },
  ];

  return (
    <>
      <div className="content">
        {/* ── Breadcrumb ─────────────────────────────────────────────────── */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Search Proposals</h2>
            
          </div>
        </div>

        {/* ── Stat Cards ─────────────────────────────────────────────────── */}
        <div className="row">
          {/* Total */}
          <div
            className="col-xl-3 col-md-6 d-flex"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setOptions(allRoles);
              setCurrentView("all");
              clearFilters();
            }}
          >
            <div className="card flex-fill">
              <div className="card-body my-card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-2">
                      <span className="p-2 br-10 bg-pink-transparent border border-pink d-flex align-items-center justify-content-center">
                        <i className="ti ti-briefcase text-pink fs-18"></i>
                      </span>
                    </div>
                    <div>
                      <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                        Total Proposals
                      </p>
                      <h4 className="counter">
                        <CountUp start={0} end={allRoles.length} duration={3} />
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Filter Bar ─────────────────────────────────────────────────── */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="row g-2 align-items-end">
              {/* Client filter */}
              <div className="col-md-3">
                <label className="form-label fs-12 mb-1">Client</label>
                <select
                  className="form-select form-select-sm"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                >
                  <option value="">All Clients</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Role Manager filter */}
              <div className="col-md-3">
                <label className="form-label fs-12 mb-1">Role Manager</label>
                <select
                  className="form-select form-select-sm"
                  value={filterManager}
                  onChange={(e) => setFilterManager(e.target.value)}
                >
                  <option value="">All Managers</option>
                  {roleManagers.map((m) => (
                    <option key={m.id} value={m.role_manager}>
                      {m.role_manager}
                    </option>
                  ))}
                </select>
              </div>
              {/* Clear */}
              <div className="col-md-3">
                <button
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={clearFilters}
                >
                  <i className="ti ti-x me-1"></i>Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Table ──────────────────────────────────────────────────────── */}
        <div className="card">
          <div className="card-body p-0">
            <div className="custom-datatable-filter table-responsive">
              <MaterialReactTable
                columns={columns}
                data={options}
                enableSorting
                initialState={{
                  pagination: { pageSize: 200 },
                  sorting: [{ id: "days_since_proposal", desc: true }],
                }}
                muiTableBodyRowProps={{ sx: { height: "20px" } }}
                muiTableBodyCellProps={{ sx: { padding: "2px 4px" } }}
                muiTableHeadCellProps={{
                  sx: { padding: "2px 4px", height: "30px" },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SearchProposalList;
